import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Save, ShieldCheck, Zap } from "lucide-react";
import EmailDefaultsPanel from "@/components/EmailDefaultsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "@/operations/lib/api";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

/** Global alert emails (not templates). Intake digest: off by default, affects-us only. */
function AlertEmailsCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.getEmailSettings().then(setS).catch(() => {}); }, []);
  const flip = async (v) => {
    setS((x) => ({ ...x, intake_alert_email: v }));
    try { await api.updateEmailSettings({ intake_alert_email: v }); toast.success(v ? "Intake alert emails on" : "Intake alert emails off"); }
    catch { toast.error("Could not save that"); setS((x) => ({ ...x, intake_alert_email: !v })); }
  };
  if (!s) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="settings-email-alerts">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">Intake alert email</div>
        <p className="text-xs text-slate-500">When an RFI or change document lands that <b>affects our scopes</b>, email the job's PM &amp; PE a short digest (what it is, the scope impact, whether a draft CO was opened). The bell alert always fires; this adds the email. Global · off by default.</p>
      </div>
      <Switch checked={!!s.intake_alert_email} onCheckedChange={flip} data-testid="settings-intake-alert-email" />
    </div>
  );
}

/** Settings > Email — the OPS-3 framework's templates and sending identity.
 *  Review-before-send is on by default on every template, and auto-send cannot be turned on
 *  until it is deliberately turned off. */
export default function EmailTemplatesPage() {
  const [data, setData] = useState(null);
  const [key, setKey] = useState("co_proposal");
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await opsApi.emailTemplates();
      setData(d);
      const t = d.templates.find((x) => x.key === key) || d.templates[0];
      setKey(t.key);
      setEdit({ subject: t.subject, body: t.body });
    } catch { toast.error("Could not load the email templates"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const pick = (k) => {
    const t = data.templates.find((x) => x.key === k);
    setKey(k);
    setEdit({ subject: t.subject, body: t.body });
  };

  const save = async (patch) => {
    // Optimistic: the box flips at once, so a fast second click cannot be lost.
    setData((d) => ({ ...d, templates: d.templates.map(
      (x) => (x.key === key ? { ...x, ...patch } : x)) }));
    setBusy(true);
    try {
      await opsApi.saveEmailTemplate(key, patch);
      toast.success("Template saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that template");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try { await opsApi.resetEmailTemplate(key); toast.success("Back to the built-in wording"); await load(); }
    catch { toast.error("Could not reset that template"); }
    finally { setBusy(false); }
  };

  if (!data || !edit) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading email templates…
    </div>;
  }

  const t = data.templates.find((x) => x.key === key);

  return (
    <div className="page-body" data-testid="settings-email-page">
      <h2 className="text-lg font-semibold text-slate-900">Email</h2>
      <AlertEmailsCard />

      <div className="mt-5 grid gap-5 lg:grid-cols-[240px_1fr]">
        <div className="space-y-1" data-testid="settings-email-list">
          {data.templates.map((x) => (
            <button key={x.key} onClick={() => pick(x.key)}
                    data-testid={`settings-email-template-${x.key}`}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      key === x.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <span>{x.name}</span>
              {x.auto_send
                ? <Zap className="h-3.5 w-3.5 text-amber-400" title="auto-send" />
                : <ShieldCheck className="h-3.5 w-3.5 opacity-60" title="review before send" />}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold text-slate-900">{t.name}</h3>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
              to the {t.audience_label.toLowerCase()}
            </span>
            <span className="text-xs text-slate-400">v{t.version}
              {t.updated_by ? ` · ${t.updated_by}` : " · built in"}</span>
          </div>

          <div className="mt-4 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={t.review_before_send} disabled={!data.can_edit || busy}
                     data-testid="settings-email-review"
                     onChange={(e) => save({ review_before_send: e.target.checked,
                                             ...(e.target.checked ? { auto_send: false } : {}) })} />
              Review before send
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={t.auto_send}
                     disabled={!data.can_edit || busy || t.review_before_send}
                     data-testid="settings-email-autosend"
                     onChange={(e) => save({ auto_send: e.target.checked })} />
              Auto-send
              {t.review_before_send && (
                <span className="text-xs text-slate-400">— turn review off first</span>
              )}
            </label>
          </div>

          <label className="mt-4 block text-xs font-medium text-slate-500">Subject</label>
          <Input value={edit.subject} disabled={!data.can_edit} className="mt-1 h-9 text-sm"
                 onChange={(e) => setEdit({ ...edit, subject: e.target.value })}
                 data-testid="settings-email-subject" />

          <label className="mt-3 block text-xs font-medium text-slate-500">Body</label>
          <Textarea value={edit.body} disabled={!data.can_edit} rows={14} className="mt-1 text-sm"
                    onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                    data-testid="settings-email-body" />

          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="settings-email-fields">
            {t.fields.map((f) => (
              <button key={f} disabled={!data.can_edit}
                      onClick={() => setEdit({ ...edit, body: `${edit.body}${f}` })}
                      className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600 hover:border-slate-400">
                {f}
              </button>
            ))}
          </div>

          {data.can_edit && (
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={reset} disabled={busy || !t.customised}
                      data-testid="settings-email-reset">
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
              </Button>
              <Button size="sm" onClick={() => save(edit)} disabled={busy}
                      data-testid="settings-email-save">
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Save className="mr-1.5 h-3.5 w-3.5" />} Save template
              </Button>
            </div>
          )}
          {!data.can_edit && (
            <p className="mt-4 text-xs text-slate-400">Only an admin can change a template.</p>
          )}

          <EmailDefaultsPanel templateKey={key} />
        </div>
      </div>
    </div>
  );
}
